import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { getCurrentUser } from "@/lib/auth";
import type { User } from "@/lib/auth";
import Navbar from "@/components/shared/layout/navbar";
import { ArrowLeft } from "lucide-react";

export default function ClientProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjectData();
  }, [id]);

  const fetchProjectData = async () => {
    try {
      setLoading(true);

      // Get current user
      const currentUser = await getCurrentUser();
      if (!currentUser) {
        navigate("/");
        return;
      }
      setUser(currentUser);

      // Fetch project
      const { data: projectData, error: projectError } = await supabase
        .from("projects")
        .select("*")
        .eq("id", id)
        .single();

      if (projectError) throw projectError;
      setProject(projectData);
    } catch (err) {
      console.error("Error fetching project:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/client/dashboard");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        {user && <Navbar user={user} />}
        <div className="flex items-center justify-center h-[80vh]">
          <div className="relative w-16 h-16">
            <div className="absolute inset-0 border-4 border-gray-800 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {user && <Navbar user={user} />}

      {/* Project Overview Header */}
      <div className="bg-white border-b-2 border-white/10 p-6">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-black hover:underline mb-4 font-bold text-sm"
          style={{ fontFamily: "'Courier New', monospace" }}
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Dashboard
        </button>

        <div className="flex items-start gap-4">
          <div className="text-6xl">{project?.thumbnail || "📁"}</div>
          <div>
            <h1
              className="text-3xl font-black text-black mb-2"
              style={{ fontFamily: "'Courier New', monospace" }}
            >
              {project?.name || "Project"}
            </h1>
            {project?.tagline && (
              <p className="text-gray-600 text-sm">{project.tagline}</p>
            )}
          </div>
        </div>
      </div>

      {/* Coming Soon Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="bg-white border-4 border-white shadow-[8px_8px_0px_0px_rgba(255,255,255,0.5)] p-8 text-center">
          <div className="text-6xl mb-6">🚧</div>
          <h2
            className="text-2xl font-black text-black mb-4 uppercase tracking-wide"
            style={{ fontFamily: "'Courier New', monospace" }}
          >
            Project Details Coming Soon
          </h2>
          <p
            className="text-gray-700 mb-6 max-w-md mx-auto"
            style={{ fontFamily: "'Courier New', monospace" }}
          >
            We're building out the full project detail page with deliverables,
            timelines, documents, and team directory.
          </p>
          <div className="space-y-2 text-left max-w-md mx-auto">
            <div
              className="flex items-center gap-2 text-sm text-gray-600"
              style={{ fontFamily: "'Courier New', monospace" }}
            >
              <span className="text-green-400">✓</span>
              <span>Project metrics and status</span>
            </div>
            <div
              className="flex items-center gap-2 text-sm text-gray-600"
              style={{ fontFamily: "'Courier New', monospace" }}
            >
              <span className="text-green-400">✓</span>
              <span>Document library</span>
            </div>
            <div
              className="flex items-center gap-2 text-sm text-gray-600"
              style={{ fontFamily: "'Courier New', monospace" }}
            >
              <span className="text-green-400">✓</span>
              <span>Team directory and contacts</span>
            </div>
            <div
              className="flex items-center gap-2 text-sm text-gray-600"
              style={{ fontFamily: "'Courier New', monospace" }}
            >
              <span className="text-green-400">✓</span>
              <span>Project timeline and milestones</span>
            </div>
            <div
              className="flex items-center gap-2 text-sm text-gray-600"
              style={{ fontFamily: "'Courier New', monospace" }}
            >
              <span className="text-green-400">✓</span>
              <span>Deliverables checklist</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
